import { useEffect, useState } from 'react'
import { AlertTriangle, Plug } from 'lucide-react'
import { apiDelete, apiGet, apiPost } from '../lib/api'
import { useTenantId } from '../context/SessionContext'
import { useToast } from '../context/ToastContext'
import {
  Button,
  DataTable,
  EmptyState,
  FormField,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  StatusBadge,
  TableToolbar,
  THead,
  Th,
  Td,
  Tr,
} from '../components/ui'

interface ConnectorPool {
  id: string
  name: string
  selection_policy: string
}

interface Connector {
  id: string
  name: string
  description: string
  pool_id: string
  is_active: boolean
  last_seen: string
  enroll_token?: string // returned only on create / regen
}

export default function AdminConnectorsPage() {
  const tenantId = useTenantId()
  const { toast } = useToast()
  const [connectors, setConnectors] = useState<Connector[]>([])
  const [pools, setPools] = useState<ConnectorPool[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ name: '', description: '', pool_id: '' })
  const [saving, setSaving] = useState(false)
  const [tokenMap, setTokenMap] = useState<Record<string, string>>({})
  const [poolFilter, setPoolFilter] = useState('')

  const fetchAll = async () => {
    if (!tenantId) return
    setLoading(true)
    setError(null)
    try {
      const [cData, pData] = await Promise.all([
        apiGet<Connector[]>(
          `/api/v1/tenants/${tenantId}/connectors${poolFilter ? `?pool_id=${poolFilter}` : ''}`,
        ),
        apiGet<ConnectorPool[]>(`/api/v1/tenants/${tenantId}/connector-pools`),
      ])
      setConnectors(cData ?? [])
      setPools(pData ?? [])
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchAll() }, [tenantId, poolFilter])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const created = await apiPost<Connector>(`/api/v1/tenants/${tenantId}/connectors`, form)
      if (created.enroll_token) {
        setTokenMap((m) => ({ ...m, [created.id]: created.enroll_token! }))
      }
      setForm({ name: '', description: '', pool_id: '' })
      setShowForm(false)
      toast({ title: 'Connector registered', variant: 'success' })
      fetchAll()
    } catch (e: any) {
      setError(e.message)
      toast({ title: 'Create failed', description: e.message, variant: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  const handleAssignPool = async (connectorID: string, newPoolID: string) => {
    try {
      await apiPost(`/api/v1/tenants/${tenantId}/connectors/${connectorID}/assign-pool`, {
        pool_id: newPoolID,
      })
      toast({ title: 'Pool assigned', variant: 'success' })
      fetchAll()
    } catch (e: any) {
      setError(e.message)
      toast({ title: 'Assign failed', description: e.message, variant: 'danger' })
    }
  }

  const handleRegenToken = async (connectorID: string) => {
    try {
      const { enroll_token } = await apiPost<{ enroll_token: string }>(
        `/api/v1/tenants/${tenantId}/connectors/${connectorID}/enroll-token`,
      )
      setTokenMap((m) => ({ ...m, [connectorID]: enroll_token }))
      toast({ title: 'Token regenerated', variant: 'success' })
    } catch (e: any) {
      setError(e.message)
      toast({ title: 'Regen failed', description: e.message, variant: 'danger' })
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this connector?')) return
    try {
      await apiDelete(`/api/v1/tenants/${tenantId}/connectors/${id}`)
      toast({ title: 'Connector deleted', variant: 'success' })
      fetchAll()
    } catch (e: any) {
      setError(e.message)
      toast({ title: 'Delete failed', description: e.message, variant: 'danger' })
    }
  }

  const poolName = (pid: string) => pools.find((p) => p.id === pid)?.name ?? '—'

  return (
    <div>
      <PageHeader
        title="Connectors"
        description="Manage secure access to resources on your private networks."
        actions={
          <Button onClick={() => setShowForm(true)}>+ New Connector</Button>
        }
      />

      {error && (
        <div className="mb-4 rounded-lg border border-danger/30 bg-danger-muted px-4 py-3 text-sm text-danger-foreground">
          {error}
        </div>
      )}

      <TableToolbar>
        <label className="text-xs text-muted">Filter by pool:</label>
        <Select
          value={poolFilter}
          onChange={(e) => setPoolFilter(e.target.value)}
          className="w-56"
        >
          <option value="">All pools</option>
          {pools.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </Select>
      </TableToolbar>

      {loading ? (
        <Skeleton className="h-48 w-full" />
      ) : connectors.length === 0 ? (
        <EmptyState
          icon={<Plug className="h-8 w-8" />}
          title="No connectors registered yet"
          description="Register a connector to generate an enroll token."
          actionLabel="New Connector"
          onAction={() => setShowForm(true)}
        />
      ) : (
        <DataTable>
          <THead>
            <Tr>
              <Th>Name</Th>
              <Th>Status</Th>
              <Th>Pool</Th>
              <Th className="text-right">Actions</Th>
            </Tr>
          </THead>
          <tbody>
            {connectors.map((c) => (
              <Tr key={c.id}>
                <Td>
                  <div>
                    <p className="font-medium">{c.name}</p>
                    <p className="font-mono text-xs text-muted">{c.id}</p>
                    {tokenMap[c.id] && (
                      <div className="mt-2 rounded-lg border border-warning/30 bg-warning-muted p-3">
                        <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-warning-foreground">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          Enroll token, copy now, shown only once:
                        </p>
                        <p className="break-all font-mono text-xs text-warning-foreground select-all">
                          {tokenMap[c.id]}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-2 h-auto px-0 text-warning-foreground"
                          onClick={() =>
                            setTokenMap((m) => {
                              const n = { ...m }
                              delete n[c.id]
                              return n
                            })
                          }
                        >
                          Dismiss
                        </Button>
                      </div>
                    )}
                  </div>
                </Td>
                <Td>
                  <StatusBadge status={c.is_active ? 'online' : 'offline'} />
                </Td>
                <Td>
                  <Select
                    value={c.pool_id ?? ''}
                    onChange={(e) => handleAssignPool(c.id, e.target.value)}
                    className="w-40"
                  >
                    <option value="">— Unassigned —</option>
                    {pools.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </Select>
                  {c.pool_id && (
                    <p className="mt-1 text-xs text-muted">{poolName(c.pool_id)}</p>
                  )}
                </Td>
                <Td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleRegenToken(c.id)}>
                      Regen Token
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-danger"
                      onClick={() => handleDelete(c.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </tbody>
        </DataTable>
      )}

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="Register Connector"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" form="connector-form" disabled={saving}>
              {saving ? 'Creating…' : 'Register & Generate Token'}
            </Button>
          </>
        }
      >
        <form id="connector-form" onSubmit={handleCreate} className="space-y-4">
          <FormField label="Name *" htmlFor="connector-name">
            <Input
              id="connector-name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. connector-chn-01"
            />
          </FormField>
          <FormField label="Assign to Pool" htmlFor="connector-pool">
            <Select
              id="connector-pool"
              value={form.pool_id}
              onChange={(e) => setForm({ ...form, pool_id: e.target.value })}
            >
              <option value="">— Unassigned —</option>
              {pools.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
          </FormField>
          <FormField label="Description" htmlFor="connector-desc">
            <Input
              id="connector-desc"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </FormField>
        </form>
      </Modal>
    </div>
  )
}
